import * as React from "react";
import {
  Select as AriaSelect,
  SelectValue as AriaSelectValue,
  Button,
  ListBox,
  ListBoxItem,
  Popover,
  type SelectProps as AriaSelectProps,
  type ListBoxItemProps,
  type SelectValueProps as AriaSelectValueProps,
} from "react-aria-components";
import { CheckIcon, ChevronDownIcon } from "lucide-react";

import { cn } from "./utils";
import { Label } from "./label";

export interface SelectProps<T extends object = object> extends Omit<AriaSelectProps<T>, "className"> {
  className?: string;
  /** Radix-compat alias for selectedKey */
  value?: string;
  /** Radix-compat alias for onSelectionChange */
  onValueChange?: (value: string) => void;
}

function Select<T extends object = object>({ className, children, value, onValueChange, ...props }: SelectProps<T>) {
  return (
    <AriaSelect
      data-slot="select"
      className={cn("group flex flex-col gap-2", className)}
      selectedKey={value ?? props.selectedKey}
      onSelectionChange={onValueChange
        ? (key) => onValueChange(String(key))
        : props.onSelectionChange}
      {...props}
    >
      {children}
    </AriaSelect>
  );
}

function SelectGroup({ children, ...props }: React.ComponentProps<"div">) {
  return <div data-slot="select-group" {...props}>{children}</div>;
}

interface SelectValueOwnProps {
  className?: string;
  /** Radix-compat: placeholder text (forwarded to parent Select in react-aria) */
  placeholder?: string;
}

function SelectValue({ className, placeholder: _placeholder, ...props }: SelectValueOwnProps & Omit<AriaSelectValueProps<object>, "className">) {
  return (
    <AriaSelectValue
      data-slot="select-value"
      className={cn("line-clamp-1 flex items-center gap-2", className)}
      {...props}
    />
  );
}

export interface SelectTriggerProps extends Omit<React.ComponentProps<typeof Button>, "children"> {
  size?: "sm" | "default";
  children?: React.ReactNode;
}

function SelectTrigger({
  className,
  size = "default",
  children,
  ...props
}: SelectTriggerProps) {
  return (
    <Button
      data-slot="select-trigger"
      data-size={size}
      className={cn(
        "border-input placeholder:text-muted-foreground [&_svg:not([class*='text-'])]:text-muted-foreground focus-visible:border-ring focus-visible:ring-ring/50 invalid:ring-destructive/20 dark:invalid:ring-destructive/40 invalid:border-destructive dark:bg-input/30 dark:hovered:bg-input/50 flex w-full items-center justify-between gap-2 rounded-md border bg-input-background px-3 py-2 text-sm whitespace-nowrap transition-[color,box-shadow] outline-none focus-visible:ring-[3px] disabled:cursor-not-allowed disabled:opacity-50 data-[size=default]:h-9 data-[size=sm]:h-8 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
        className,
      )}
      {...props}
    >
      {children}
      <ChevronDownIcon className="size-4 opacity-50" />
    </Button>
  );
}

export interface SelectContentProps extends React.ComponentProps<typeof Popover> {
  position?: "item-aligned" | "popper";
}

function SelectContent({
  className,
  children,
  position = "popper",
  ...props
}: SelectContentProps) {
  return (
    <Popover
      data-slot="select-content"
      className={cn(
        "bg-popover text-popover-foreground entering:animate-in exiting:animate-out exiting:fade-out-0 entering:fade-in-0 exiting:zoom-out-95 entering:zoom-in-95 placement-bottom:slide-in-from-top-2 placement-left:slide-in-from-right-2 placement-right:slide-in-from-left-2 placement-top:slide-in-from-bottom-2 relative z-50 max-h-60 min-w-[8rem] overflow-x-hidden overflow-y-auto rounded-md border shadow-md",
        position === "popper" && "placement-bottom:translate-y-1 placement-top:-translate-y-1",
        className,
      )}
      {...props}
    >
      <ListBox className="p-1 outline-none">
        {children}
      </ListBox>
    </Popover>
  );
}

function SelectLabel({ className, ...props }: React.ComponentProps<typeof Label>) {
  return (
    <Label
      data-slot="select-label"
      className={cn("text-muted-foreground px-2 py-1.5 text-xs", className)}
      {...props}
    />
  );
}

export interface SelectItemProps extends Omit<ListBoxItemProps, "className" | "id" | "value" | "children"> {
  className?: string;
  /** Radix-compat alias for id */
  value?: string;
  id?: ListBoxItemProps["id"];
  children?: React.ReactNode;
}

function SelectItem({ className, children, value, ...props }: SelectItemProps) {
  return (
    <ListBoxItem
      data-slot="select-item"
      id={value ?? props.id}
      textValue={typeof children === "string" ? children : props.textValue}
      className={cn(
        "focused:bg-accent focused:text-accent-foreground [&_svg:not([class*='text-'])]:text-muted-foreground relative flex w-full cursor-default items-center gap-2 rounded-sm py-1.5 pr-8 pl-2 text-sm outline-hidden select-none disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
        className,
      )}
      {...props}
    >
      {({ isSelected }) => (
        <>
          <span className="absolute right-2 flex size-3.5 items-center justify-center">
            {isSelected && <CheckIcon className="size-4" />}
          </span>
          {children}
        </>
      )}
    </ListBoxItem>
  );
}

function SelectSeparator({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="select-separator"
      className={cn("bg-border pointer-events-none -mx-1 my-1 h-px", className)}
      {...props}
    />
  );
}

export {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
};
